/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import CheckoutBoxHeader from "../../CheckoutBoxHeader";
import UserCheckoutAddresses from "../../../user-checkout-helpers/user-receiving-methods/UserCheckoutAddresses";
import ReceivingTime from "@/app/components/pages-components/checkout/checkout-helpers/receiving-settings/receiving-settings-helpers/receiving-time/ReceivingTime";
import DisabledMethodMSg from "../../checkout-error-messages/DisabledMethodMSg";
import { CheckoutIcons } from "../../../checkout-icons/GetCheckoutSvg";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { IField } from "@/api/AppApi/services/builder/interfaces/interfaces";
import ReceivingAccordion from "./ReceivingAccordion";
import DeliveryBranches from "./DeliveryBranches";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { RecievingFields } from "@/app/components/pages-components/checkout/checkout-helpers/receiving-settings/receiving-settings-helpers/helpers";
import { useAtom } from "jotai";
import { isSignedInAtom } from "@/hooks/authAtoms";
import GuestAddressSection from "./GuestAddressSection";
import CheckoutErrMsg from "../../checkout-error-messages/CheckoutErrMsg";
import { useGetDeliveryBranches } from "@/api/AppApi/services/cart";
import {
  disableSubmitBtnAtom,
  NoAvailableBranchAtom,
  receivingBranchAtom,
} from "@/hooks/receivingHeaderAtom";

interface props {
  enabled: boolean;
  isMobile: boolean;
  addressIdFieldName: string;
  timeField: IField;
  form: UseFormReturn<FieldValues, any>;
  shipping_not_available_message: string;
}
const DeliverySection: React.FC<props> = ({
  enabled,
  isMobile,
  addressIdFieldName,
  form,
  timeField,
  shipping_not_available_message,
}) => {
  const branchFieldName = RecievingFields.preferredBranchFieldName;
  const t = useTranslation();
  const [isSignin] = useAtom(isSignedInAtom);
  const addressId = form.watch(RecievingFields.addressIdFieldName);
  const areaId = form.watch(RecievingFields.areaField)?.key;
  const cityId = form.watch(RecievingFields.cityField)?.key;
  const countryId = form.watch(RecievingFields.counteryField)?.key;
  const [receivingBranch, setreceivingBranch] = useAtom(receivingBranchAtom);
  const [, setDisableSubmitBtn] = useAtom(disableSubmitBtnAtom);
  const [noAvailableBranch, setNoAvailableBranch] = useAtom(
    NoAvailableBranchAtom,
  );
  const getDeliveryBranches = useGetDeliveryBranches({
    enabled: !!addressId || !!areaId,
    address_id: addressId!,
    area_id: areaId!,
  });
  const branches = getDeliveryBranches?.data?.result ?? [];
  const disableSelectBranch =
    !branches.length || !areaId || !cityId || !countryId;
  const noBranches = getDeliveryBranches?.data?.result?.length === 0;
  const isSingleBranch = branches.length < 2;

  // Auto-select single branch
  useEffect(() => {
    if (isSingleBranch && branches[0]) {
      setreceivingBranch(branches[0]);
      form.setValue(branchFieldName, branches[0].id, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [isSingleBranch, branches, branchFieldName]);

  useEffect(() => {
    setDisableSubmitBtn(disableSelectBranch);
    setNoAvailableBranch(noBranches);
  }, [disableSelectBranch, noBranches]);

  // Create accordion items array, conditionally excluding the branch selection
  const accordionItems = [
    {
      accordionHeader: (
        <CheckoutBoxHeader
          svg={CheckoutIcons.popUpDelivery}
          title={t.delivery_address}
          mobile={isMobile}
          withoutBorder
          fontSize={16}
        />
      ),
      accordionBody: isSignin ? (
        <UserCheckoutAddresses
          form={form}
          fieldName={addressIdFieldName}
          mobile={isMobile}
        />
      ) : (
        <GuestAddressSection form={form} />
      ),
      disabled: false,
      others: noAvailableBranch && (
        <CheckoutErrMsg
          message={
            t.sorry_there_is_no_branch_serving_the_area_you_selected_Please_choose_another_area
          }
        />
      ),
    },
    // Only include branch selection accordion if there's more than one branch
    ...(isSingleBranch
      ? []
      : [
          {
            accordionHeader: (
              <CheckoutBoxHeader
                svg={CheckoutIcons.pickup}
                title={t.preferred_branch}
                mobile={isMobile}
                withoutBorder
                fontSize={16}
              />
            ),
            accordionBody: (
              <DeliveryBranches
                form={form}
                fieldName={branchFieldName}
                mobile={isMobile}
                branches={branches!}
              />
            ),
            disabled: disableSelectBranch,
            others: form.formState.errors[
              RecievingFields.preferredBranchFieldName
            ]?.message && (
              <CheckoutErrMsg
                message={
                  form.formState.errors[
                    RecievingFields.preferredBranchFieldName
                  ]?.message as string
                }
              />
            ),
          },
        ]),
    ...(!disableSelectBranch
      ? [
          {
            accordionHeader: (
              <CheckoutBoxHeader
                svg={CheckoutIcons.popUpDeliveryTime}
                title={t.delivery_time}
                mobile={isMobile}
                withoutBorder
                fontSize={16}
              />
            ),
            accordionBody: (
              <ReceivingTime
                form={form}
                timeField={timeField}
                timeTypeFieldName={RecievingFields.deliveryTimeTypeFieldName}
              />
            ),
            disabled: !receivingBranch,
          },
        ]
      : []),
  ];

  return enabled ? (
    <ReceivingAccordion accordionItems={accordionItems} />
  ) : (
    <DisabledMethodMSg message={shipping_not_available_message} />
  );
};

export default DeliverySection;
